/**
 * Org-scoped News Feed (CVA-4718) — Redux thunks
 *
 * Mirrors the pagination-thunk pattern already used by `redux/action/post.action.ts`
 * (`fetchPostPaginated` / `fetchMorePosts`), but backed by the `news-feed`
 * service wrapper so the Dashboard widget and the full News Feed page can
 * share one slice (`redux/slices/news-feed.slice.ts`) independently of the
 * legacy `post` slice.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import i18n from '../../i18n/i18n';
import { ICreateSubjectError } from '../../interface/subjectModel';
import {
  ICreatePost,
  IPostQuery,
  IPostListResponse,
  IPostCreateResponse,
  IPostEditResponse,
  IPostDeleteResponseBody,
} from '../../interface/postModel';
import {
  fetchNewsFeedPosts,
  createNewsFeedPost as createNewsFeedPostService,
  editNewsFeedPost as editNewsFeedPostService,
  deleteNewsFeedPost as deleteNewsFeedPostService,
  toggleNewsFeedPostLike as toggleNewsFeedPostLikeService,
} from '../../services/news-feed';

/** Fetches page 1 (initial load / refresh) — the slice replaces its list. */
export const fetchNewsFeedPostsPaginated = createAsyncThunk<
  IPostListResponse,
  IPostQuery,
  { rejectValue: ICreateSubjectError }
>('news-feed/fetchPaginated', async (query, { rejectWithValue }) => {
  try {
    return await fetchNewsFeedPosts(query);
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_fetch_posts', 'Failed to fetch posts'),
      });
    }
    const genericError = err as { status?: number; message?: string };
    return rejectWithValue({
      status: genericError?.status || 500,
      message: genericError?.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/** Fetches page > 1 for infinite scroll — the slice appends to its list. */
export const fetchMoreNewsFeedPosts = createAsyncThunk<
  IPostListResponse,
  IPostQuery,
  { rejectValue: ICreateSubjectError }
>('news-feed/fetchMore', async (query, { rejectWithValue }) => {
  try {
    return await fetchNewsFeedPosts(query);
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message:
          error.response?.data?.message ||
          i18n.t('toast.failed_to_fetch_more_posts', 'Failed to fetch more posts'),
      });
    }
    const genericError = err as { status?: number; message?: string };
    return rejectWithValue({
      status: genericError?.status || 500,
      message: genericError?.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/** Toggles like state. State itself is updated optimistically by the caller. */
export const toggleNewsFeedPostLike = createAsyncThunk<
  void,
  string,
  { rejectValue: ICreateSubjectError }
>('news-feed/like', async (postId, { rejectWithValue }) => {
  try {
    await toggleNewsFeedPostLikeService(postId);
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    }
    const genericError = err as { status?: number; message?: string };
    return rejectWithValue({
      status: genericError?.status,
      message: genericError?.message,
    });
  }
});

export const createNewsFeedPost = createAsyncThunk<
  IPostCreateResponse,
  ICreatePost,
  { rejectValue: ICreateSubjectError }
>('news-feed/create', async (body, { rejectWithValue }) => {
  try {
    return await createNewsFeedPostService(body);
  } catch (err: any) {
    // HTTP 413 is surfaced by the multipart interceptor as { status: 413, message }.
    if (err?.status === 413 || err?.response?.status === 413) {
      return rejectWithValue({
        status: 413,
        message: err?.message || i18n.t('post.upload_too_large'),
      });
    }
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    }
    return rejectWithValue({ status: err?.status, message: err.message });
  }
});

export const editNewsFeedPost = createAsyncThunk<
  IPostEditResponse,
  FormData,
  { rejectValue: ICreateSubjectError }
>('news-feed/edit', async (body, { rejectWithValue }) => {
  try {
    return await editNewsFeedPostService(body);
  } catch (err: any) {
    if (err?.status === 413 || err?.response?.status === 413) {
      return rejectWithValue({
        status: 413,
        message: err?.message || i18n.t('post.upload_too_large'),
      });
    }
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    }
    return rejectWithValue({ status: err?.status, message: err.message });
  }
});

export const deleteNewsFeedPostThunk = createAsyncThunk<
  IPostDeleteResponseBody,
  string,
  { rejectValue: ICreateSubjectError }
>('news-feed/delete', async (id, { rejectWithValue }) => {
  try {
    return await deleteNewsFeedPostService(id);
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    }
    return rejectWithValue({ status: err?.status, message: err.message });
  }
});
